import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, qs } from "./client";
import type {
  AuditEntry, Contact, Country, CustomValues, FieldDefinition, Me, Page, Role, User,
} from "./types";

export const keys = {
  me: ["me"] as const,
  contacts: (q: string) => ["contacts", q] as const,
  contact: (id: string) => ["contact", id] as const,
  fields: (objectType: string) => ["fields", objectType] as const,
  users: ["users"] as const,
  roles: ["roles"] as const,
  audit: (objectType: string, id: string) => ["audit", objectType, id] as const,
};

export const useMe = (enabled = true) =>
  useQuery({ queryKey: keys.me, queryFn: () => api<Me>("GET", "/me"), enabled, staleTime: 60_000 });

/** Save your own name, time zone and language. */
export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { full_name?: string; timezone?: string | null; locale?: string | null; phone?: string | null }) =>
      api<Me>("PATCH", "/me", data),
    onSuccess: (me) => { qc.setQueryData(keys.me, me); void qc.invalidateQueries(); },
  });
}

/** Countries with built-in tax rules (reference data, cached for the session). */
export const useCountries = () =>
  useQuery({ queryKey: ["meta", "countries"], queryFn: () => api<Country[]>("GET", "/meta/countries"), staleTime: Infinity });

export const useContacts = (q: string) =>
  useInfiniteQuery({
    queryKey: keys.contacts(q),
    queryFn: ({ pageParam }) =>
      api<Page<Contact>>("GET", `/contacts${qs({ q, cursor: pageParam, limit: 25 })}`),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
  });

export const useContact = (id: string) =>
  useQuery({ queryKey: keys.contact(id), queryFn: () => api<Contact>("GET", `/contacts/${id}`) });

export interface ContactInput {
  first_name?: string; last_name?: string | null; email?: string | null; phone?: string | null;
  job_title?: string | null; company_name?: string | null; custom?: CustomValues;
  whatsapp_opt_in?: boolean; email_opt_out?: boolean; consent_source?: string;
}

export function useSaveContact(id?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ContactInput) =>
      id ? api<Contact>("PATCH", `/contacts/${id}`, data) : api<Contact>("POST", "/contacts", data),
    onSuccess: (contact) => {
      qc.setQueryData(keys.contact(contact.id), contact);
      void qc.invalidateQueries({ queryKey: ["contacts"] });
      void qc.invalidateQueries({ queryKey: ["audit", "contact", contact.id] });
      void qc.invalidateQueries({ queryKey: ["timeline", "contact", contact.id] });
      void qc.invalidateQueries({ queryKey: ["picker"] });
    },
  });
}

export function useDeleteContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>("DELETE", `/contacts/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["contacts"] }),
  });
}

export const useFields = (objectType: string) =>
  useQuery({
    queryKey: keys.fields(objectType),
    queryFn: () => api<FieldDefinition[]>("GET", `/fields/${objectType}`),
  });

export function useCreateField() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Omit<FieldDefinition, "id" | "position" | "help_text"> & { help_text?: string | null }) =>
      api<FieldDefinition>("POST", "/fields", data),
    onSuccess: (f) => void qc.invalidateQueries({ queryKey: keys.fields(f.object_type) }),
  });
}

export function useDeleteField(objectType: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>("DELETE", `/fields/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.fields(objectType) }),
  });
}

export const useUsers = () => useQuery({ queryKey: keys.users, queryFn: () => api<User[]>("GET", "/users") });
export const useRoles = () => useQuery({ queryKey: keys.roles, queryFn: () => api<Role[]>("GET", "/roles") });

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { email: string; full_name: string; password: string; role: string; dealer_account_id?: string | null }) =>
      api<User>("POST", "/users", data),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.users }),
  });
}

export const useAudit = (objectType: string, id: string, enabled: boolean) =>
  useQuery({
    queryKey: keys.audit(objectType, id),
    queryFn: () => api<AuditEntry[]>("GET", `/audit-logs${qs({ object_type: objectType, object_id: id })}`),
    enabled,
  });
